import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'

import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import * as Yup from 'yup'

import DialogHeader from '@/shared/DialogHeader'
import Typography from '@mui/material/Typography'
import unionBy from 'lodash/unionBy'
import debounce from '@mui/material/utils/debounce'


const mapStateToProps = ({ loading }) => ({
  isLoading: loading['plan/createPlan'],
  isPlanLoading: loading['plan/getPlanLevel'],
  isCompanyLoading: loading['account/getCompany'],
})

const inputConfig = {
  inputBaseProps: {
    labelSuffix: '', // 不加冒號或其他符號
    justifyContent: 'center', // 將 label 與 input 靠上對齊
    direction: 'column', // label 在上，input 在下
    alignItems: 'stretch', // input 撐滿整個欄位
    labelProps: {
      sx: {
        mb: -2, // 調小 label 與 input 的距離
        lineHeight: 1.2, // 行高
        fontWeight: 400, // 適中，不要太輕
        fontSize: 12, // 確保字體大小統一
      },
    },
    childrenSx: {
      width: '95%',
      mt: 0, // 去掉 input 上方的多餘間距
    },
  },
  color: 'btn_positive',
  endAdornment: args => {
    const value = args?.value || ''
    const maxLength = args?.inputProps?.maxLength || 0

    if (!maxLength) return null

    return (
      <Typography
        variant="caption"
        sx={{ color: '#00000040', fontSize: '12px !important' }}
      >{`${value.length}/${maxLength}`}</Typography>
    )
  },
}

const CreatePlanDialog = ({
  dispatch,
  isLoading,
  isCompanyLoading,
  isPlanLoading,
  onClose,
  onComplete,
  ...props
}) => {
  const form = useRef()
  const { t } = useTranslation()
  const companyPageRef = useRef(0)
  const [companyResults, setCompanyResults] = useState({})
  const { data: companyResultsData = [] } = companyResults
  const [planResults, setPlanResults] = useState([])
  const [planMap, setPlanMap] = useState({})


  const handleClose = () => {
    const { reset } = form.current
    reset?.()
    onClose?.()
  }

  const handleSubmit = () => {
    const { handleSubmit: formSubmit } = form.current

    formSubmit?.(values => {
      const {
        companyName: { value: companyId },
        planId,
        customEmailCount,
        customPreEmailCount,
        status,
      } = values

      const selectedPlan = planMap?.[planId]

      const payload = {
        companyId,
        planId,
        status,
      }

      // ✅ 只有人工修改才加進 payload
      if (
        selectedPlan &&
        Number(customEmailCount) !== Number(selectedPlan.emailCount)
      ) {
        payload.customEmailCount = customEmailCount
      }

      if (
        selectedPlan &&
        Number(customPreEmailCount) !== Number(selectedPlan.preEmailCount)
      ) {
        payload.customPreEmailCount = customPreEmailCount
      }

      dispatch({
        type: 'plan/createPlan',
        payload,
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: { message: t('messages.plan_created'), severity: 'success' },
          })
          onClose?.()
          onComplete?.()
        },
        errorCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.plan_create_failed'),
              severity: 'error',
            },
          })
        },
      })
    })?.()
  }

  const getCompany = params => {
    const { page = 0, keyword = '' } = params || {}

    if (!isCompanyLoading) {
      companyPageRef.current = page

      dispatch({
        type: 'account/getCompany',
        payload: {
          pageIndex: companyPageRef.current,
          pageSize: 10,
          keyword, //  關鍵字
        },
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({
            value: d.companyId,
            label: d.companyName,
          }))

          setCompanyResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
            paging: responsePaging,
          }))
        },
      })
    }
  }

  const fetchCompanyData = useCallback((companyNameValue) => {
    const keyword = companyNameValue || form.current?.getValues()?.companyName?.label
    const payload = {
      pageIndex: companyPageRef.current,
      pageSize: 10,
      keyword: keyword || '',
    }

    // 回傳 dispatch 的 Promise
    return new Promise(resolve => {
      dispatch({
        type: 'account/getCompany',
        payload,
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({
            value: d.companyId,
            label: d.companyName,
            planId: d.planId,
          }))

          setCompanyResults(s => ({
            data: pageIndex > 0
              ? unionBy(s.data || [], newData, 'value')
              : newData,
            paging: responsePaging,
          }))

          resolve(newData)  //  將資料 resolve 出去
        },
      })
    })
  }, [dispatch])

  const setCompanyNameDebounce = debounce((_, value) => {
    companyPageRef.current = 0
    fetchCompanyData(value)
  }, 400)

  const getPlanList = () => {
    if (!isPlanLoading) {
      dispatch({
        type: 'plan/getPlanLevel',
        successCallback: response => {
          const responseData = response?.data || []

          const planOptions = responseData.map(plan => ({
            value: plan.id,
            label: plan.name,
          }))

          const map = {}
          responseData.forEach(plan => {
            map[plan.id] = plan
          })

          setPlanResults(planOptions)
          setPlanMap(map)

          const defaultPlan = map[0]

          if (defaultPlan && form.current) {
            form.current.setValue?.('customEmailCount', defaultPlan.emailCount)
            form.current.setValue?.('customPreEmailCount', defaultPlan.preEmailCount)
          }
        }
      })
    }
  }

  useEffect(() => {
    if (props.open) {
      getPlanList()
      getCompany()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open])


  // // 監控 planId 和 planMap
  // useEffect(() => {
  //   if (form.current) {
  //     const { planId } = form.current.getValues()
  //     if (planId && planMap[planId]) {
  //       form.current.setValue('emailCount', planMap[planId].emailCount)
  //       form.current.setValue('customPreEmailCount', planMap[planId].customPreEmailCount)
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [planMap, form.current?.getValues()?.planId])


  return (
    <Dialog {...props} maxWidth={'xs'}
      PaperProps={{
        sx: {
          borderRadius: 5, // ← 圓角在這裡才有效
          overflow: 'hidden', //  必加，不然 Header 會蓋掉圓角
        },
      }}>

      <DialogHeader
        title={t('general.add_company_plan')}
        onClose={handleClose}
      />

      <DialogContent sx={{ width: 450, pr: 1 }}>
        <FormCreator
          ref={form}
          gridItemProps={{ md: 12 }}
          formConfig={{ mode: 'submit' }}
          formItems={[
            {
              label: t('general.company_name'),
              name: 'companyName',
              type: 'autocomplete',
              defaultValue: '',
              validator: Yup.mixed()
                .test(
                  'required',
                  t('validation.company_required'),
                  value => value !== null && value !== undefined && value !== ''
                ),
              inputConfig: {
                ...inputConfig,
                options: companyResultsData,
                isOptionEqualToValue: (option, value) => option?.value === value?.value,
                onChange: selectedOption => {
                  form.current.setValue('companyName', selectedOption || null) //  同步到 form
                  companyPageRef.current = 0
                  // fetchCompanyData(selectedOption?.label).then(companyData => {
                  //   const { planId } = companyData?.[0] || {}
                  //   form.current.setValue('planId', planId || null)
                  // })
                },
                onInputChange: (_, inputValue) => {
                  setCompanyNameDebounce(_, inputValue) //  debounce 處理 keyword
                },
              },
            },
            {
              label: t('general.plan'),
              name: 'planId',
              type: 'select',
              defaultValue: null,
              validator: Yup.mixed()
                .test(
                  'required',
                  t('validation.plan_required'),
                  value => value !== null && value !== undefined && value !== ''
                ),
              inputConfig: {
                ...inputConfig,
                options: planResults,
                onChange: e => {
                  const selectedValue = Number(
                    e?.target?.value ?? e ?? 0
                  )
                  const selectedPlan = planMap[selectedValue]

                  if (selectedPlan && form.current) {
                    form.current.setValue?.('customEmailCount', selectedPlan.emailCount)
                    form.current.setValue?.('customPreEmailCount', selectedPlan.preEmailCount)
                  }
                }
              },
            },
            {
              label: t('general.send_quantity'),
              name: 'customEmailCount',
              defaultValue: '',
              validator: Yup.number()
                .typeError(t('validation.send_quantity_required'))
                .required(t('validation.send_quantity_required'))
                .when('planId', {
                  is: '6',
                  then: schema => schema.min(0),
                  otherwise: schema => schema.min(1, t('validation.send_quantity_min')),
                }),
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.test_quantity'),
              name: 'customPreEmailCount',
              defaultValue: '',
              validator: Yup.number()
                .typeError(t('validation.test_quantity_required'))
                .required(t('validation.test_quantity_required'))
                .when('planId', {
                  is: '6',
                  then: schema => schema.min(0),
                  otherwise: schema => schema.min(1, t('validation.test_quantity_min')),
                }),
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.status'),
              name: 'status',
              type: 'select',
              defaultValue: 'A',
              inputConfig: {
                ...inputConfig,
                options: [
                  { label: t('status.ACTIVE'), value: 'A' },
                  { label: t('status.INACTIVE'), value: 'IA' },
                ],
              },
            },
          ].filter(Boolean)}
          submitComponent={() => null}
          resetComponent={() => null}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'flex-end', //  右下角
        }}>
        <Button
          style={{ minWidth: 100, backgroundColor: '#2D2D6C' }}
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            handleSubmit()
            // onClose()
          }}
        >
          {t('general.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(CreatePlanDialog)